// src/components/common/ReactionPicker.tsx
// Composant de réactions style Facebook avec animations
import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Tooltip, Typography, Popover, Grow } from '@mui/material';

export type ReactionType = 'like' | 'love' | 'pray' | 'amen' | 'sad';

export interface ReactionConfig {
  type: ReactionType;
  emoji: string;
  label: string;
  color: string;
}

export const REACTIONS: ReactionConfig[] = [
  { type: 'like', emoji: '👍', label: "J'aime", color: '#2196F3' },
  { type: 'love', emoji: '❤️', label: "J'adore", color: '#E91E63' },
  { type: 'pray', emoji: '🙏', label: 'Prière', color: '#9C27B0' },
  { type: 'amen', emoji: '✝️', label: 'Amen', color: '#FF9800' },
  { type: 'sad', emoji: '😢', label: 'Triste', color: '#607D8B' },
];

export const getReactionConfig = (type: ReactionType): ReactionConfig => {
  return REACTIONS.find(r => r.type === type) || REACTIONS[0];
};

interface ReactionPickerProps {
  currentReaction?: ReactionType | null;
  onReact: (type: ReactionType | null) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  showLabel?: boolean;
}

const ReactionPicker: React.FC<ReactionPickerProps> = ({
  currentReaction,
  onReact,
  disabled = false,
  size = 'medium',
  showLabel = true,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [hoveredReaction, setHoveredReaction] = useState<ReactionType | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const isOpen = Boolean(anchorEl);
  const currentConfig = currentReaction ? getReactionConfig(currentReaction) : null;

  // Handle hover open
  const handleMouseEnter = () => {
    if (disabled) return;
    timeoutRef.current = setTimeout(() => {
      setAnchorEl(buttonRef.current);
    }, 500); // Delay before showing picker
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handlePopoverMouseLeave = () => {
    setAnchorEl(null);
    setHoveredReaction(null);
  };

  // Handle click (toggle like or show picker)
  const handleClick = () => {
    if (disabled) return;
    if (currentReaction) {
      // Remove reaction
      onReact(null);
    } else {
      // Quick like
      onReact('like');
    }
  };

  // Handle reaction selection
  const handleSelectReaction = (type: ReactionType) => {
    onReact(type);
    setAnchorEl(null);
    setHoveredReaction(null);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const emojiSize = size === 'small' ? '1.2rem' : '1.5rem';
  const pickerEmojiSize = size === 'small' ? '1.5rem' : '2rem';

  return (
    <>
      <IconButton
        ref={buttonRef}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        disabled={disabled}
        size={size}
        sx={{
          color: currentConfig?.color || 'text.secondary',
          '&:hover': {
            backgroundColor: currentConfig
              ? `${currentConfig.color}15`
              : 'action.hover',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {currentConfig ? (
            <Typography component="span" sx={{ fontSize: emojiSize, lineHeight: 1 }}>
              {currentConfig.emoji}
            </Typography>
          ) : (
            <Typography component="span" sx={{ fontSize: emojiSize, lineHeight: 1 }}>
              👍
            </Typography>
          )}
          {showLabel && (
            <Typography
              variant="body2"
              sx={{
                fontWeight: currentConfig ? 600 : 400,
                color: currentConfig?.color || 'text.secondary',
                ml: 0.5,
              }}
            >
              {currentConfig?.label || "J'aime"}
            </Typography>
          )}
        </Box>
      </IconButton>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverMouseLeave}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            onMouseLeave: handlePopoverMouseLeave,
            sx: {
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              p: 0.5,
              bgcolor: 'background.paper',
            },
          },
        }}
        disableRestoreFocus
      >
        <Box
          sx={{
            display: 'flex',
            gap: 0.5,
            p: 0.5,
          }}
        >
          {REACTIONS.map((reaction, index) => (
            <Grow
              key={reaction.type}
              in={isOpen}
              timeout={150 + index * 50}
            >
              <Tooltip title={reaction.label} placement="top" arrow>
                <Box
                  onClick={() => handleSelectReaction(reaction.type)}
                  onMouseEnter={() => setHoveredReaction(reaction.type)}
                  onMouseLeave={() => setHoveredReaction(null)}
                  sx={{
                    cursor: 'pointer',
                    fontSize: pickerEmojiSize,
                    p: 0.75,
                    borderRadius: '50%',
                    transition: 'all 0.2s ease',
                    transform: hoveredReaction === reaction.type
                      ? 'scale(1.3) translateY(-4px)'
                      : 'scale(1)',
                    bgcolor: hoveredReaction === reaction.type
                      ? `${reaction.color}15`
                      : 'transparent',
                    '&:hover': {
                      bgcolor: `${reaction.color}15`,
                    },
                  }}
                >
                  {reaction.emoji}
                </Box>
              </Tooltip>
            </Grow>
          ))}
        </Box>
      </Popover>
    </>
  );
};

// Composant pour afficher le résumé des réactions
interface ReactionSummaryProps {
  reactions: { [key in ReactionType]?: number };
  totalCount: number;
  onClick?: () => void;
  size?: 'small' | 'medium';
}

export const ReactionSummary: React.FC<ReactionSummaryProps> = ({
  reactions,
  totalCount,
  onClick,
  size = 'medium',
}) => {
  if (totalCount === 0) return null;

  // Get top 3 reactions by count
  const sortedReactions = Object.entries(reactions)
    .filter(([_, count]) => count && count > 0)
    .sort((a, b) => (b[1] || 0) - (a[1] || 0))
    .slice(0, 3);

  const emojiSize = size === 'small' ? '0.875rem' : '1rem';

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? { textDecoration: 'underline' } : {},
      }}
    >
      <Box sx={{ display: 'flex', ml: -0.5 }}>
        {sortedReactions.map(([type], index) => {
          const config = getReactionConfig(type as ReactionType);
          return (
            <Box
              key={type}
              sx={{
                fontSize: emojiSize,
                ml: index > 0 ? -0.5 : 0,
                zIndex: sortedReactions.length - index,
                bgcolor: 'background.paper',
                borderRadius: '50%',
                border: '2px solid',
                borderColor: 'background.paper',
                lineHeight: 1,
                p: 0.25,
              }}
            >
              {config.emoji}
            </Box>
          );
        })}
      </Box>
      <Typography variant="body2" color="text.secondary">
        {totalCount}
      </Typography>
    </Box>
  );
};

export default ReactionPicker;
